import 'bootstrap/dist/css/bootstrap.min.css';
import { format } from 'date-fns/format';
import { useEffect } from 'react';
import { Container, Table } from 'react-bootstrap';
import styled from 'styled-components';
import { Result } from '../models/Result';
import { getTodayResults } from '../redux/entities/resultEntity';
import { useAppDispatch, useAppSelector } from '../redux/hooks';


const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;
const BurfiResultContainer = styled.div`
  display: grid;
  grid-template-areas:
    ".  n1  ."
    "n4 nc n2"
    ".  n3  .";
  gap: 5px;
  width: fit-content;
  margin: 0 auto;
`;

// Add center position to BurfiDigit
const BurfiDigit = styled.div`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  &[data-position="n1"] { grid-area: n1; }
  &[data-position="n2"] { grid-area: n2; }
  &[data-position="n3"] { grid-area: n3; }
  &[data-position="n4"] { grid-area: n4; }
  &[data-position="nc"] { grid-area: nc; background-color: #f0f0f0; }
`;

const groupBySiteName = (data: Result[]): { [key: string]: Result[] } => {
  return data.reduce((groups: { [key: string]: Result[] }, item: Result) => {
    if (!item.gameDTO?.name) return groups;
    const group = groups[item.gameDTO.name] || [];
    group.push(item);
    groups[item.gameDTO.name] = group;
    return groups;
  }, {});
};

const ResultPage = () => {
  const todayResults = useAppSelector(state => state.entities.resultReducer.todayResults)
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getTodayResults())
  }, [dispatch]);

  const renderResult = (result: Result) => {
    if (result.gameDTO?.burfiSupported) {
      const digits = result.burfiResult?.split('') || [];
      const sum = digits.reduce((acc, digit) => acc + (parseInt(digit) || 0), 0);
      const centerDigit = sum.toString().slice(-1);
      
      return (
        <BurfiResultContainer>
          <BurfiDigit data-position="n1">{digits[0] || '-'}</BurfiDigit>
          <BurfiDigit data-position="n2">{digits[1] || '-'}</BurfiDigit>
          <BurfiDigit data-position="n3">{digits[2] || '-'}</BurfiDigit>
          <BurfiDigit data-position="n4">{digits[3] || '-'}</BurfiDigit>
          <BurfiDigit data-position="nc">{centerDigit}</BurfiDigit>
        </BurfiResultContainer>
      );
    }
    return `${result.pattiResult}*${result.singleResult}`;
  };

  // Function to parse time string (e.g., '9:30 AM') into minutes since midnight for sorting
  const timeToMinutes = (timeStr: string) => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  return (
    <Container>
      <PageHeading>Today's Result</PageHeading>
      {/* Today Result */}
      <section className="mb-5">
        {Object.entries(groupBySiteName(todayResults)).map(([siteName, results]) => {
          // Sort results by resultTime
          const sortedResults = [...results].sort((a, b) => {
            const timeA = a.resultTime?.resultTime ? timeToMinutes(a.resultTime.resultTime) : 0;
            const timeB = b.resultTime?.resultTime ? timeToMinutes(b.resultTime.resultTime) : 0;
            return timeA - timeB;
          });
          
          return (
            <div key={siteName}>
              <div className="p-3 text-uppercase font-weight-bold">
                <b>{siteName}</b>
              </div>
              <div className="table-responsive">
                <Table bordered striped>
                  <thead>
                    <tr>
                      <th>Date</th>
                      {sortedResults.map((result) => (
                        <th key={result.resultId}>{result.resultTime?.resultTime}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{format(new Date(sortedResults[0]?.resultDate), 'dd-MM-yyyy')}</td>
                      {sortedResults.map((result) => (
                        <td key={result.resultId}>
                          {renderResult(result)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          );
        })}
      </section>
    </Container>
  );
};

export default ResultPage;
